 
import React, { useEffect, useCallback, useMemo } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useFormValidation } from 'domains/form-validation';
import { FormFieldProps } from 'types/form-field';
import { callAll } from 'utils/function';
import { getSchemaFieldAttributes } from 'utils/fieldAttributes';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FormField = <C extends React.ComponentType<any>>({
  name,
  component: Component,
  disabled,
  onChange,
  onBlur,
  valueFormatter,
  ...props
}: FormFieldProps<C>): JSX.Element => {
  const { setError, setValue, clearErrors, register, unregister } = useFormContext();
  const { errors, isSubmitted, isSubmitting } = useFormState({ name, exact: true });
  const { schema, disabled: formDisabled, serverErrors } = useFormValidation();
  const schemaFieldAttributes = useMemo(() => getSchemaFieldAttributes(schema, name), [schema, name]);
  const serverError = serverErrors[name];

  useEffect(() => {
    if (serverError) {
      setError(name, { type: 'server', message: serverError });
    }
  }, [serverError, name, setError]);

  useEffect(() => {
    return () => {
      unregister(name);
    };
  }, [unregister, name]);

  const clearFieldValue = useCallback(
    () => setValue(name, '', { shouldValidate: isSubmitted }),
    [name, isSubmitted, setValue],
  );

  const setFieldValue = useCallback(
    (value: string) => setValue(name, value, { shouldValidate: isSubmitted }),
    [name, isSubmitted, setValue],
  );

  const setFieldError = useCallback(
    (message: string | null) => {
      if (message) {
        setError(name, { type: 'validate', message });
      } else {
        clearErrors(name);
      }
    },
    [name, setError, clearErrors],
  );

  const { onChange: formOnChange, onBlur: formOnBlur, ...formProps } = register(name);

  const callbacks = {
    onChange: callAll(onChange, formOnChange),
    onBlur: callAll(onBlur, formOnBlur),
  };

  return (
    <Component
      {...props}
      {...formProps}
      {...callbacks}
      {...schemaFieldAttributes}
      disabled={formDisabled || isSubmitting || disabled || false}
      error={errors[name]?.message}
      clearValue={clearFieldValue}
      setValue={setFieldValue}
      setError={setFieldError}
    />
  );
};
